import React, { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'

// Components
import { Select } from '../Select'

/**
 * Builds the URL for one page of results, keeping whatever query `baseUrl` already has.
 * @param {string} baseUrl list endpoint, with or without a query string of its own
 * @param {number} page 1-based page number
 * @param {string} searchTerm server-side search, sent as `q`
 * @returns url
 */
const buildPageUrl = (baseUrl, page, searchTerm) => {
  const params = new URLSearchParams({ page })
  if (searchTerm) params.set('q', searchTerm)

  return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params.toString()}`
}

/**
 *
 * PaginatedSelect
 *
 * - A `Select` over a paginated list endpoint. Search runs on the server, through the
 *   endpoint's `q` filter, so it covers every record rather than only the loaded page.
 * - The next page loads when the list is scrolled to its end.
 * - Any other `Select` prop (`customButton`, `multiSelect`, `dataTestId`, ...) passes through.
 */
const PaginatedSelect = ({
  baseUrl,
  className,
  disabled,
  error,
  name,
  request,
  transformOption,
  ...selectProps
}) => {
  // State
  const [options, setOptions] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [nextPage, setNextPage] = useState(null)
  const [totalCount, setTotalCount] = useState(null)
  const [loadingInitialData, setLoadingInitialData] = useState(true)
  const [loadingNextPage, setLoadingNextPage] = useState(false)
  const [errorLoadingData, setErrorLoadingData] = useState(null)

  // Ref
  // Only the latest request may update the list. A search typed while an earlier one (or
  // a next page) is still in flight would otherwise be overwritten by the stale response.
  const latestRequestId = useRef(0)
  const hasLoaded = useRef(false)

  /**
   * Loads one page of options. Page 1 replaces the list; later pages append to it.
   * @param {number} page
   * @param {string} term
   */
  const loadPage = async (page, term) => {
    latestRequestId.current += 1
    const requestId = latestRequestId.current

    // Only the very first load disables the select. Disabling it for a search would close
    // the open list under the person typing into it.
    const setLoading = hasLoaded.current ? setLoadingNextPage : setLoadingInitialData

    setErrorLoadingData(null)
    const response = await request(
      buildPageUrl(baseUrl, page, term),
      setErrorLoadingData,
      setLoading,
      () => {},
    )

    if (requestId !== latestRequestId.current) return
    hasLoaded.current = true

    if (response) {
      const pageOptions = _.map(response.results, transformOption)

      setOptions((current) =>
        page === 1 ? pageOptions : _.uniqBy([...current, ...pageOptions], 'id'),
      )
      setTotalCount(response.count)
      setNextPage(response.next ? page + 1 : null)
    }
  }

  useEffect(() => {
    loadPage(1, searchTerm)
  }, [baseUrl, searchTerm])

  const debouncedSetSearchTerm = useMemo(() => _.debounce(setSearchTerm, 400), [])

  useEffect(() => () => debouncedSetSearchTerm.cancel(), [debouncedSetSearchTerm])

  // A validation message for the field. A failure to load is not one: it is shown in the
  // open list instead, so a select inside a table header does not grow an error box that
  // covers the header around it.
  const configureError = () => {
    if (_.isString(error)) return error
    if (error) return 'This field is required'

    return null
  }

  return (
    <Select
      className={className}
      disabled={loadingInitialData || disabled}
      emptyMessage={errorLoadingData ? 'Unable to load options.' : null}
      error={configureError()}
      id={name}
      options={options}
      pagination={{
        attemptToLoadNextPage: () => {
          if (!loadingNextPage && nextPage) loadPage(nextPage, searchTerm)
        },
        hasNext: nextPage !== null,
        loading: loadingNextPage,
        totalCount,
      }}
      search
      setSearchTerm={debouncedSetSearchTerm}
      style={{ flex: true, width: '100%' }}
      {...selectProps}
    />
  )
}

PaginatedSelect.defaultProps = {
  className:
    'rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple',
  disabled: false,
  error: null,
}

PaginatedSelect.propTypes = {
  baseUrl: PropTypes.string.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  // A message, or anything truthy for "This field is required" (a react-hook-form error)
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.object]),
  name: PropTypes.string.isRequired,
  request: PropTypes.func.isRequired,
  transformOption: PropTypes.func.isRequired,
}

export default PaginatedSelect
